import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Save, Image as ImageIcon, Palette } from "lucide-react";

const ORIGIN = API.replace(/\/api$/, "");
export const brandAsset = (url) => (url ? `${ORIGIN}${url}` : "");

const IDENTITY = [
  ["trading_name", "Trading Name"], ["tagline", "Tagline"], ["website", "Website"],
  ["email", "General Email"], ["phone", "Phone"], ["abn", "ABN"], ["acn", "ACN"],
  ["address", "Business Address"], ["director_name", "Director Name"], ["director_position", "Director Position"],
  ["footer_text", "Footer Text"], ["social_links", "Social Links"],
];
const COLORS = [
  ["primary_color", "Primary"], ["secondary_color", "Secondary"], ["accent_color", "Accent"],
  ["background_color", "Background"], ["heading_color", "Heading"], ["button_color", "Button"],
];
const LOGOS = [
  ["logo_full_url", "Primary Logo"], ["logo_shield_url", "Sidebar / Compact Logo"],
  ["logo_email_url", "Email Logo"], ["favicon_url", "Favicon"],
];

export default function Branding() {
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/branding").then((r) => setB(r.data)); }, []);
  if (!b) return <div className="text-slate-400">Loading…</div>;

  const set = (k, v) => setB((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/settings/branding", b); toast.success("Branding saved. It flows into the app immediately."); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const uploadLogo = async (slot, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/settings/branding/logo/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(slot, r.data.url);
      toast.success("Logo uploaded");
    } catch (e) { toast.error("Upload failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="branding-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Branding</h1>
          <p className="text-slate-500 mt-1">Your visual identity — used across the app, emails and documents.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy/90" data-testid="branding-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save Branding"}</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Logos */}
          <section className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h2 className="font-heading font-semibold text-navy flex items-center gap-2 mb-4"><ImageIcon className="w-5 h-5" />Logos</h2>
            <div className="grid grid-cols-2 gap-4">
              {LOGOS.map(([slot, label]) => (
                <div key={slot} className="border border-slate-100 rounded-xl p-3">
                  <p className="text-xs font-semibold text-slate-500 mb-2">{label}</p>
                  <div className="h-16 flex items-center justify-center bg-slate-50 rounded-lg mb-2 overflow-hidden">
                    {b[slot] ? <img src={brandAsset(b[slot])} alt={label} className="max-h-14 object-contain" /> : <span className="text-xs text-slate-300">No image</span>}
                  </div>
                  <label className="flex items-center justify-center gap-1.5 text-xs text-navy font-medium cursor-pointer hover:underline">
                    <Upload className="w-3.5 h-3.5" />Upload
                    <input type="file" accept="image/*" className="hidden" data-testid={`branding-upload-${slot}`}
                      onChange={(e) => e.target.files[0] && uploadLogo(slot, e.target.files[0])} />
                  </label>
                </div>
              ))}
            </div>
          </section>

          {/* Identity */}
          <section className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h2 className="font-heading font-semibold text-navy mb-4">Company Identity</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {IDENTITY.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <Input value={b[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" data-testid={`branding-${k}`} />
                </div>
              ))}
            </div>
          </section>

          {/* Colours */}
          <section className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h2 className="font-heading font-semibold text-navy flex items-center gap-2 mb-4"><Palette className="w-5 h-5" />Brand Colours</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {COLORS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={b[k] || "#000000"} onChange={(e) => set(k, e.target.value)} className="w-10 h-9 rounded border border-slate-200 cursor-pointer" data-testid={`branding-color-${k}`} />
                    <Input value={b[k] || ""} onChange={(e) => set(k, e.target.value)} className="flex-1 font-mono text-xs" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Live preview */}
        <div className="space-y-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Preview</p>
          <div className="rounded-2xl border border-slate-100 overflow-hidden shadow-sm" data-testid="branding-preview">
            <div className="p-5 text-white" style={{ background: b.primary_color }}>
              {b.logo_full_url ? <img src={brandAsset(b.logo_full_url)} alt="logo" className="h-12 object-contain mb-2" /> : <div className="font-heading font-bold text-lg">{b.trading_name}</div>}
              <p className="text-xs opacity-80">{b.tagline}</p>
            </div>
            <div className="p-5 space-y-3" style={{ background: b.background_color }}>
              <h3 className="font-heading font-bold" style={{ color: b.heading_color }}>{b.trading_name}</h3>
              <p className="text-xs text-slate-500">{b.address || "—"}</p>
              <p className="text-xs text-slate-500">{b.phone} · {b.email}</p>
              <button className="px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: b.button_color }}>Sample Button</button>
              <div className="flex gap-2 pt-1">
                {COLORS.map(([k]) => <div key={k} className="w-6 h-6 rounded-full border border-white shadow" style={{ background: b[k] }} title={k} />)}
              </div>
            </div>
          </div>
          <p className="text-xs text-slate-400">Logo &amp; trading name apply to the sidebar and login immediately after saving. Full colour theming and PDF/email branding land with Document Templates (a later phase).</p>
        </div>
      </div>
    </div>
  );
}
